/**
 * Recent Searches Service
 *
 * Maintaining the state of the Recent Searches
 */

import MessageBusTopics from '../../../../common/MessageBusTopics';

/**
 * Recent Searches constructor
 *
 * @constructor
 */
function SearchRecentService() {
    /**
     * Local storage key
     *
     * @type {String}
     */
    this.storageKey = 'search.recentSearches';

    /**
     * localForage - the storage
     *
     * @type {null}
     */
    this.localForage = undefined;

    /**
     * Sets the maximum number of items that should be returned by getItems()
     *
     * Set 0 for no limitation.
     *
     * @type {Number}
     */
    this.getItemsLimit = 5;

    /**
     * State of recent searches
     *
     * @type {Array}
     */
    this.recentSearches = undefined;

    window.WH.messageBus.subscribe(MessageBusTopics.LOCAL_FORAGE, function (topic, message) {
        if (message && message.setItem && typeof message.setItem === 'function') {
            this.localForage = message;
            this.recentSearches = this.getItems();
        }
    }.bind(this));
}

/**
 * Initialise a new RecentSearch item object
 *
 * @param {String} searchTerm
 *
 * @returns {Object}
 */
SearchRecentService.prototype.initNewRecentSearch = function (searchTerm) {
    var time = (new Date()).getTime();

    return {
        term: searchTerm,
        count: 1,
        lastUpdatedAt: time,
    };
};

/**
 * Adds an item to the recent search storage
 *
 * @param {String} searchTerm
 *
 * @returns {void}
 *
 * @public
 */
SearchRecentService.prototype.addItem = function (searchTerm) {
    var getSearchTerm = function (item) {
        return item.term;
    };
    if (typeof this.recentSearches !== 'undefined') {
        var recentSearchIndex = this.recentSearches.map(getSearchTerm).indexOf(searchTerm);
        var time = (new Date()).getTime();

        if (recentSearchIndex !== -1) {
            this.recentSearches[recentSearchIndex].count += 1;
            this.recentSearches[recentSearchIndex].lastUpdatedAt = time;
        } else {
            this.recentSearches.push(this.initNewRecentSearch(searchTerm));
        }

        // Another sorting stategy has coded. Have a look on: compareForMyPopularSearches()
        this.recentSearches.sort(this.compareForMyRecentSearches.bind(this));
        this.saveToStorage();
    }
};

/**
 * Returns a certain amount of item. It loads from the storage if not available yet.
 *
 * @returns {Array}
 *
 * @public
 */
SearchRecentService.prototype.getItems = function () {

    if (typeof this.recentSearches === 'undefined') {
        this.loadFromStorage();
    }

    if (!!this.recentSearches && this.getItemsLimit > 0) {
        return this.recentSearches.slice(0, this.getItemsLimit);
    } else {
        return this.recentSearches;
    }
};

/**
 * Compare callback for Array.sort() function
 *
 * @param {Object} a
 * @param {Object} b
 *
 * @returns {Number}
 *
 * @private
 */
SearchRecentService.prototype.compareForMyRecentSearches = function (a, b) {
    return b.lastUpdatedAt - a.lastUpdatedAt;
};

/**
 * Saves the recent searches to the storage
 *
 * @returns {void}
 *
 * @private
 */
SearchRecentService.prototype.saveToStorage = function () {
    if (typeof this.localForage !== 'undefined') {
        this.localForage.setItem(this.storageKey, JSON.stringify(this.recentSearches));
    }
};

/**
 * Loads the recent searches from the storage
 *
 * The `localForage` returns `null` if the key does not exists. See `successHandler()` below.
 *
 *
 * @returns {void}
 *
 * @private
 */
SearchRecentService.prototype.loadFromStorage = function () {
    var fallbackValue = [];
    var errorHandler = function () {
        this.recentSearches = fallbackValue;
    };
    var successHandler = function (storedValue) {
        if (storedValue) {
            this.recentSearches = JSON.parse(storedValue);
        } else {
            this.recentSearches = fallbackValue;
        }
    };

    if (typeof this.localForage !== 'undefined') {
        this.localForage.getItem(this.storageKey)
            .then(successHandler.bind(this))
            .catch(errorHandler.bind(this));
    }
};

export default SearchRecentService;
